import * as React from "react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { makeStyles } from "@fluentui/react-components";
import { useQueryClient } from "@tanstack/react-query";

import {
  DataGrid,
  DataGridHeader,
  DataGridHeaderCell,
  DataGridBody,
  DataGridRow,
  DataGridCell,
  TableCellLayout,
  createTableColumn,
  Badge,
} from "@fluentui/react-components";

// Components
import { ErrorMessagesBar } from "../../../shared/components/ErrorMessagesBar";
import Spinner from "../../../shared/components/Spinner";

// Business logic
import { useSelectedMailboxesContext } from "../context/SelectedMailboxContext";
import { useMailboxesQuery } from "../hooks/useMailboxQueries";

const useStyles = makeStyles({
  container: {
    height: "calc(100dvh - 200px)", // adjust based on layout
    display: "flex",
    flexDirection: "column",
    boxSizing: "border-box",
  },
  scrollArea: {
    flex: 1,
    overflowY: "auto",
    overflowX: "auto",
  },
  stickyHeaderRow: {
    position: "sticky",
    top: 0,
    zIndex: 2,
    display: "flex",
  },
});

const MailboxesDataGrid = () => {
  const { t } = useTranslation();
  const styles = useStyles();
  const { value: selectedMailboxes, setValue: setSelectedMailboxes } = useSelectedMailboxesContext();
  const queryClient = useQueryClient();

  // Errors
  const [errors, setErrors] = useState([]);
  const [monitorStatuses, setMonitorStatuses] = useState({});

  const { data = [], isFetching, error } = useMailboxesQuery();

  // Effects
  useEffect(() => {
    const newErrors = [];
    if (error) {
      newErrors.push({ message: error?.message || t("generic.error"), type: "error", details: error });
    }
    if (newErrors.length > 0) {
      setErrors((prevErrors) => [...prevErrors, ...newErrors]);
    }
  }, [error, t]);

  if (isFetching) return <Spinner label={t("generic.loading")} />;

  const columns = [
    createTableColumn({
      columnId: "name",
      renderHeaderCell: () => t("generic.name"),
      renderCell: (item) => <TableCellLayout>{item.name}</TableCellLayout>,
    }),
    createTableColumn({
      columnId: "email_address",
      renderHeaderCell: () => t("generic.email"),
      renderCell: (item) => <TableCellLayout>{item.email_address}</TableCellLayout>,
    }),
    createTableColumn({
      columnId: "protocol",
      renderHeaderCell: () => t("generic.protocol"),
      renderCell: (item) => <TableCellLayout>{item.protocol}</TableCellLayout>,
    }),
    createTableColumn({
      columnId: "enabled",
      renderHeaderCell: () => "Enabled",
      renderCell: (item) => (
        <TableCellLayout>
          <Badge appearance={item.enabled ? "filled" : "outline"} color={item.enabled ? "success" : "subtle"}>
            {item.enabled ? "Yes" : "No"}
          </Badge>
        </TableCellLayout>
      ),
    }),
    createTableColumn({
      columnId: "active",
      renderHeaderCell: () => "Active",
      renderCell: (item) => (
        <TableCellLayout>
          <span>{item.active}</span>
          <Badge appearance={item.active ? "filled" : "outline"} color={item.active ? "success" : "subtle"}>
            {t(item.active ? "generic.yes" : "generic.no")}
          </Badge>
        </TableCellLayout>
      ),
    }),
    createTableColumn({
      columnId: "incoming_host",
      renderHeaderCell: () => "Incoming Host",
      renderCell: (item) => <TableCellLayout>{item.incoming_host}</TableCellLayout>,
    }),
    createTableColumn({
      columnId: "outgoing_host",
      renderHeaderCell: () => "Outgoing Host",
      renderCell: (item) => <TableCellLayout>{item.outgoing_host}</TableCellLayout>,
    }),
  ];

  const columnSizingOptions = {
    name: {
      minWidth: 150,
      defaultWidth: 200,
    },
    email_address: {
      minWidth: 200,
      defaultWidth: 250,
    },
    protocol: {
      minWidth: 80,
      defaultWidth: 100,
    },
    enabled: {
      minWidth: 80,
      defaultWidth: 80,
    },
    active: {
      minWidth: 80,
      defaultWidth: 100,
    },
    incoming_host: {
      minWidth: 150,
      defaultWidth: 200,
    },
    outgoing_host: {
      minWidth: 150,
      defaultWidth: 200,
    },
  };

  const handleSelectionChange = (event, selectionData) => {
    const ids = [...selectionData.selectedItems];
    const newSelection = data.filter((l) => ids.includes(l.id));
    setSelectedMailboxes(newSelection);
  };

  return (
    <div className={styles.container}>
      <ErrorMessagesBar errors={errors} onDismiss={() => setErrors([])} />
      <DataGrid
        items={data}
        columns={columns}
        columnSizingOptions={columnSizingOptions}
        selectedItems={selectedMailboxes.map((item) => item.id)}
        getRowId={(item) => item.id}
        focusMode="composite"
        resizableColumns
        selectionMode="single"
        onSelectionChange={handleSelectionChange}
      >
        <DataGridHeader>
          <DataGridRow
            className={styles.stickyHeaderRow}
            selectionCell={{
              checkboxIndicator: { "aria-label": "Select all rows" },
            }}
          >
            {({ renderHeaderCell }) => <DataGridHeaderCell>{renderHeaderCell()}</DataGridHeaderCell>}
          </DataGridRow>
        </DataGridHeader>

        <div className={styles.scrollArea}>
          <DataGridBody>
            {({ item, rowId }) => (
              <DataGridRow
                key={rowId}
                selectionCell={{
                  checkboxIndicator: { "aria-label": "Select row" },
                }}
              >
                {({ renderCell }) => <DataGridCell>{renderCell(item)}</DataGridCell>}
              </DataGridRow>
            )}
          </DataGridBody>
        </div>
      </DataGrid>
    </div>
  );
};

export default MailboxesDataGrid;
